import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { apiClient } from "@/lib/api-client";

export function useNotes(claimId: number) {
    const { firebaseUser } = useAuth();
    const queryClient = useQueryClient();

    const { data: notes, isLoading } = useQuery({
        queryKey: ["claim-notes", claimId],
        queryFn: async () => {
            if (!claimId) return [];
            const response = await apiClient.notes.listNotes({ claimId });
            return response.notes || [];
        },
        enabled: !!claimId && !!firebaseUser,
    });

    const createNoteMutation = useMutation({
        mutationFn: async (content: string) => {
            if (!claimId || isNaN(claimId)) throw new Error("Invalid Claim ID");
            if (!content.trim()) throw new Error("Note content cannot be empty");

            return await apiClient.notes.createNote({
                createNoteRequest: {
                    claimId,
                    text: content
                }
            });
        },
        onSuccess: () => {
            // Invalidate notes query, not activities
            queryClient.invalidateQueries({ queryKey: ["claim-notes", claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const deleteNoteMutation = useMutation({
        mutationFn: async (noteId: number) => {
            await apiClient.notes.deleteNote({ noteId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-notes", claimId] });
        }
    });

    const updateNoteMutation = useMutation({
        mutationFn: async ({ noteId, text }: { noteId: number; text: string }) => {
            return await apiClient.notes.updateNote({
                noteId,
                updateNoteRequest: { text }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-notes", claimId] });
        }
    });

    return {
        notes: notes || [],
        isLoading,
        createNote: createNoteMutation.mutate,
        isCreating: createNoteMutation.isPending,
        updateNote: updateNoteMutation.mutate,
        isUpdating: updateNoteMutation.isPending,
        deleteNote: deleteNoteMutation.mutate,
        isDeleting: deleteNoteMutation.isPending
    };
}
